import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const TABS = [
  { id: 'company', label: 'Company Info', icon: 'Building2' },
  { id: 'logo', label: 'Organization Logo', icon: 'Image' },
  { id: 'invoice', label: 'Invoice Header', icon: 'FileText' },
  { id: 'quote', label: 'Quote Header', icon: 'ClipboardList' },
  { id: 'ticket', label: 'Ticket Header', icon: 'Wrench' },
  { id: 'payment', label: 'Payment Info', icon: 'CreditCard' }
];

// Defined OUTSIDE the main component to prevent remounting on every render
const Field = ({ label, field, type = 'text', placeholder = '', rows, form, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
    {rows ? (
      <textarea
        value={form?.[field] || ''}
        onChange={(e) => onChange(field, e?.target?.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
      />
    ) : (
      <input
        type={type}
        value={form?.[field] || ''}
        onChange={(e) => onChange(field, e?.target?.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
      />
    )}
  </div>
);

const SectionHeader = ({ title, description }) => (
  <div className="mb-6">
    <h3 className="text-base font-semibold text-foreground">{title}</h3>
    {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
  </div>
);

const OrganizationSettings = () => {
  const [activeTab, setActiveTab] = useState('company');
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({});
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');
  const logoInputRef = useRef(null);

  useEffect(() => { fetchOrg(); }, []);

  const fetchOrg = async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase?.from('organizations')?.select('*')?.limit(1)?.single();
      if (err && err?.code !== 'PGRST116') throw err;
      if (data) {
        setOrg(data);
        setForm(data);
      } else {
        const defaults = {
          name: '', slug: 'my-company',
          address_line1: '', city: '', state: '', zip_code: '', country: 'US',
          phone: '', email: '', website: '',
          logo_url: '',
          invoice_header_company_name: '', invoice_header_address: '',
          invoice_header_phone: '', invoice_header_email: '', invoice_header_website: '',
          invoice_payment_terms: 'Net 30', invoice_payment_instructions: '',
          invoice_bank_name: '', invoice_bank_account: '', invoice_bank_routing: '',
          invoice_notes: '',
          quote_header_company_name: '', quote_header_address: '',
          quote_header_phone: '', quote_header_email: '',
          quote_validity_days: 30, quote_terms: '', quote_notes: '',
          ticket_header_company_name: '', ticket_header_address: '',
          ticket_header_phone: '', ticket_header_email: '', ticket_footer_text: '',
          primary_color: '#2563eb'
        };
        setForm(defaults);
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file?.type?.startsWith('image/')) {
      setLogoError('Please select an image file (JPG, PNG, GIF, WebP).');
      return;
    }
    if (file?.size > 5 * 1024 * 1024) {
      setLogoError('Image must be smaller than 5MB.');
      return;
    }
    setLogoUploading(true);
    setLogoError('');
    try {
      const compressed = await compressImage(file);
      const ext = file?.name?.split('.')?.pop()?.toLowerCase() || 'jpg';
      const fileName = `org-logo-${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase?.storage
        ?.from('ticket-photos')
        ?.upload(`logos/${fileName}`, compressed, { upsert: true, contentType: file?.type });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase?.storage?.from('ticket-photos')?.getPublicUrl(`logos/${fileName}`);
      const logoUrl = urlData?.publicUrl;
      handleChange('logo_url', logoUrl);
      if (org?.id) {
        await supabase?.from('organizations')?.update({ logo_url: logoUrl, updated_at: new Date()?.toISOString() })?.eq('id', org?.id);
      }
    } catch (err) {
      setLogoError(err?.message || 'Failed to upload logo.');
    } finally {
      setLogoUploading(false);
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX = 800;
          let w = img.width;
          let h = img.height;
          if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
            else { w = Math.round(w * MAX / h); h = MAX; }
          }
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob((blob) => resolve(blob), file?.type, 0.85);
        };
        img.src = e?.target?.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveLogo = async () => {
    handleChange('logo_url', '');
    if (org?.id) {
      await supabase?.from('organizations')?.update({ logo_url: '', updated_at: new Date()?.toISOString() })?.eq('id', org?.id);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      if (org?.id) {
        const { error: err } = await supabase?.from('organizations')?.update({
          ...form, updated_at: new Date()?.toISOString()
        })?.eq('id', org?.id);
        if (err) throw err;
      } else {
        const slug = form?.name?.toLowerCase()?.replace(/[^a-z0-9]/g, '-')?.replace(/-+/g, '-') || 'my-company';
        const { data, error: err } = await supabase?.from('organizations')?.insert({
          ...form, slug
        })?.select()?.single();
        if (err) throw err;
        setOrg(data);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const renderPreview = () => {
    const companyName = form?.invoice_header_company_name || form?.name || 'Your Company Name';
    const address = form?.invoice_header_address || `${form?.address_line1 || ''} ${form?.city || ''} ${form?.state || ''}`;
    return (
      <div className="bg-white border border-border rounded-xl p-5 mt-6">
        <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">Document Header Preview</p>
        <div className="border-b-2 pb-4 mb-4" style={{ borderColor: form?.primary_color || '#2563eb' }}>
          <div className="flex items-start justify-between">
            <div>
              {form?.logo_url && (
                <img src={form?.logo_url} alt="Organization logo" className="h-12 w-auto object-contain mb-2" />
              )}
              <h2 className="text-lg font-bold text-foreground">{companyName}</h2>
              {address?.trim() && <p className="text-sm text-muted-foreground mt-0.5">{address?.trim()}</p>}
              {(form?.invoice_header_phone || form?.phone) && (
                <p className="text-sm text-muted-foreground">{form?.invoice_header_phone || form?.phone}</p>
              )}
              {(form?.invoice_header_email || form?.email) && (
                <p className="text-sm text-muted-foreground">{form?.invoice_header_email || form?.email}</p>
              )}
            </div>
            <div className="text-right">
              {!form?.logo_url && (
                <div className="w-16 h-16 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${form?.primary_color || '#2563eb'}20` }}>
                  <Icon name="Wrench" size={28} color={form?.primary_color || '#2563eb'} />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>INVOICE #INV-0001</span>
          <span>Date: {new Date()?.toLocaleDateString()}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Organization Settings - RIGJOCKEY</title></Helmet>
      <Header />
      <main className="pt-6 pb-16 px-4 max-w-4xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Building2" size={22} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Organization Settings</h1>
              <p className="text-sm text-muted-foreground">Customize your company info and document headers</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar Tabs */}
            <div className="lg:col-span-1">
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                {TABS?.map(tab => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors border-b border-border last:border-0 ${
                      activeTab === tab?.id
                        ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} color="currentColor" />
                    {tab?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3">
              <div className="bg-card rounded-xl border border-border p-6">
                {/* Company Info */}
                {activeTab === 'company' && (
                  <div className="space-y-4">
                    <SectionHeader title="Company Information" description="Basic details about your organization" />
                    <Field label="Company Name *" field="name" placeholder="RIGJOCKEY Field Services LLC" form={form} onChange={handleChange} />
                    <Field label="Address Line 1" field="address_line1" placeholder="1234 Industrial Blvd" form={form} onChange={handleChange} />
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="City" field="city" placeholder="Houston" form={form} onChange={handleChange} />
                      <Field label="State" field="state" placeholder="TX" form={form} onChange={handleChange} />
                      <Field label="ZIP Code" field="zip_code" placeholder="77001" form={form} onChange={handleChange} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Phone" field="phone" placeholder="(713) 555-0100" form={form} onChange={handleChange} />
                      <Field label="Email" field="email" type="email" placeholder="info@company.com" form={form} onChange={handleChange} />
                    </div>
                    <Field label="Website" field="website" placeholder="www.company.com" form={form} onChange={handleChange} />
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Brand Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={form?.primary_color || '#2563eb'}
                          onChange={(e) => handleChange('primary_color', e?.target?.value)}
                          className="w-12 h-10 rounded-lg border border-border cursor-pointer"
                        />
                        <input
                          type="text"
                          value={form?.primary_color || '#2563eb'}
                          onChange={(e) => handleChange('primary_color', e?.target?.value)}
                          className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Organization Logo */}
                {activeTab === 'logo' && (
                  <div className="space-y-6">
                    <SectionHeader title="Organization Logo" description="Upload your logo to display on dashboards and all documents (quotes, invoices, tickets, emails)" />
                    
                    {/* Current Logo Preview */}
                    <div className="bg-muted/30 rounded-xl border border-border p-6">
                      <p className="text-sm font-medium text-foreground mb-4">Current Logo</p>
                      {form?.logo_url ? (
                        <div className="flex items-start gap-4">
                          <div className="bg-white border border-border rounded-xl p-4 flex items-center justify-center" style={{ minWidth: 160, minHeight: 100 }}>
                            <img
                              src={form?.logo_url}
                              alt="Organization logo"
                              className="max-h-24 max-w-full object-contain"
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <p className="text-sm text-muted-foreground">Logo is set and will appear on all documents and dashboards.</p>
                            <button
                              onClick={handleRemoveLogo}
                              className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors w-fit"
                            >
                              <Icon name="Trash2" size={14} color="currentColor" />
                              Remove Logo
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center bg-white border-2 border-dashed border-border rounded-xl p-10">
                          <div className="text-center">
                            <Icon name="Image" size={40} color="var(--color-muted-foreground)" />
                            <p className="mt-2 text-sm text-muted-foreground">No logo uploaded yet</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Upload Area */}
                    <div>
                      <p className="text-sm font-medium text-foreground mb-3">Upload New Logo</p>
                      <div
                        className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                        onClick={() => logoInputRef?.current?.click()}
                      >
                        {logoUploading ? (
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            <p className="text-sm text-muted-foreground">Uploading logo...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                              <Icon name="Upload" size={24} color="var(--color-primary)" />
                            </div>
                            <p className="text-sm font-medium text-foreground">Click to upload logo</p>
                            <p className="text-xs text-muted-foreground">PNG, JPG, GIF, WebP — max 5MB. Recommended: 400×200px or wider</p>
                          </div>
                        )}
                      </div>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoUpload}
                      />
                      {logoError && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5">
                          <Icon name="AlertCircle" size={14} color="currentColor" />
                          {logoError}
                        </p>
                      )}
                    </div>

                    {/* Usage Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-start gap-2">
                        <Icon name="Info" size={16} color="#2563eb" />
                        <div>
                          <p className="text-sm font-medium text-blue-800 mb-1">Where your logo appears</p>
                          <ul className="text-sm text-blue-700 space-y-0.5 list-disc list-inside">
                            <li>Dashboard and My Dashboard header</li>
                            <li>Quote PDFs and documents</li>
                            <li>Invoice PDFs and documents</li>
                            <li>Service ticket PDFs</li>
                            <li>Email templates</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Invoice Header */}
                {activeTab === 'invoice' && (
                  <div className="space-y-4">
                    <SectionHeader title="Invoice Header" description="Customize how your company appears on invoices" />
                    <Field label="Company Name on Invoice" field="invoice_header_company_name" placeholder="Same as company name if blank" form={form} onChange={handleChange} />
                    <Field label="Address on Invoice" field="invoice_header_address" placeholder="Full address as it appears on invoice" form={form} onChange={handleChange} />
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Phone on Invoice" field="invoice_header_phone" placeholder="(713) 555-0100" form={form} onChange={handleChange} />
                      <Field label="Email on Invoice" field="invoice_header_email" type="email" placeholder="billing@company.com" form={form} onChange={handleChange} />
                    </div>
                    <Field label="Website on Invoice" field="invoice_header_website" placeholder="www.company.com" form={form} onChange={handleChange} />
                    <Field label="Payment Terms" field="invoice_payment_terms" placeholder="Net 30" form={form} onChange={handleChange} />
                    <Field label="Invoice Notes" field="invoice_notes" placeholder="Thank you for your business!" rows={3} form={form} onChange={handleChange} />
                    {renderPreview()}
                  </div>
                )}

                {/* Quote Header */}
                {activeTab === 'quote' && (
                  <div className="space-y-4">
                    <SectionHeader title="Quote Header" description="Customize how your company appears on quotes" />
                    <Field label="Company Name on Quote" field="quote_header_company_name" placeholder="Same as company name if blank" form={form} onChange={handleChange} />
                    <Field label="Address on Quote" field="quote_header_address" placeholder="Full address as it appears on quote" form={form} onChange={handleChange} />
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Phone on Quote" field="quote_header_phone" placeholder="(713) 555-0100" form={form} onChange={handleChange} />
                      <Field label="Email on Quote" field="quote_header_email" type="email" placeholder="sales@company.com" form={form} onChange={handleChange} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Quote Validity (days)</label>
                      <input
                        type="number"
                        value={form?.quote_validity_days || 30}
                        onChange={(e) => handleChange('quote_validity_days', parseInt(e?.target?.value) || 30)}
                        min={1}
                        className="w-32 px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                    <Field label="Quote Terms & Conditions" field="quote_terms" placeholder="Quote valid for 30 days from issue date..." rows={4} form={form} onChange={handleChange} />
                    <Field label="Quote Notes" field="quote_notes" placeholder="Additional notes for quotes..." rows={3} form={form} onChange={handleChange} />
                  </div>
                )}

                {/* Ticket Header */}
                {activeTab === 'ticket' && (
                  <div className="space-y-4">
                    <SectionHeader title="Ticket / Work Order Header" description="Customize how your company appears on service tickets" />
                    <Field label="Company Name on Ticket" field="ticket_header_company_name" placeholder="Same as company name if blank" form={form} onChange={handleChange} />
                    <Field label="Address on Ticket" field="ticket_header_address" placeholder="Full address as it appears on ticket" form={form} onChange={handleChange} />
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Phone on Ticket" field="ticket_header_phone" placeholder="(713) 555-0100" form={form} onChange={handleChange} />
                      <Field label="Email on Ticket" field="ticket_header_email" type="email" placeholder="service@company.com" form={form} onChange={handleChange} />
                    </div>
                    <Field label="Ticket Footer Text" field="ticket_footer_text" placeholder="Thank you for choosing our services. For questions call..." rows={3} form={form} onChange={handleChange} />
                  </div>
                )}

                {/* Payment Info */}
                {activeTab === 'payment' && (
                  <div className="space-y-4">
                    <SectionHeader title="Payment Information" description="Banking and payment details shown on invoices" />
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                      <div className="flex items-start gap-2">
                        <Icon name="AlertTriangle" size={16} color="#d97706" />
                        <p className="text-sm text-yellow-700">Payment details are shown on invoices. Only enter information you want customers to see.</p>
                      </div>
                    </div>
                    <Field label="Payment Instructions" field="invoice_payment_instructions" placeholder="Please make checks payable to... ACH payments accepted..." rows={3} form={form} onChange={handleChange} />
                    <div className="grid grid-cols-1 gap-4">
                      <Field label="Bank Name" field="invoice_bank_name" placeholder="First National Bank" form={form} onChange={handleChange} />
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Account Number (last 4)" field="invoice_bank_account" placeholder="****4521" form={form} onChange={handleChange} />
                        <Field label="Routing Number (last 4)" field="invoice_bank_routing" placeholder="****0021" form={form} onChange={handleChange} />
                      </div>
                    </div>
                    <Field label="Default Payment Terms" field="invoice_payment_terms" placeholder="Net 30" form={form} onChange={handleChange} />
                  </div>
                )}

                {/* Save Button */}
                {activeTab !== 'logo' && (
                  <div className="mt-6 pt-6 border-t border-border flex items-center justify-between">
                    {error && (
                      <p className="text-sm text-red-600 flex items-center gap-1.5">
                        <Icon name="AlertCircle" size={14} color="currentColor" />
                        {error}
                      </p>
                    )}
                    {saved && (
                      <p className="text-sm text-green-600 flex items-center gap-1.5">
                        <Icon name="CheckCircle" size={14} color="currentColor" />
                        Settings saved successfully!
                      </p>
                    )}
                    {!error && !saved && <div />}
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center gap-2"
                    >
                      {saving ? (
                        <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                      ) : (
                        <><Icon name="Save" size={16} color="currentColor" /> Save Settings</>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default OrganizationSettings;
